import { useRef } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Box, Button, Chip, CircularProgress, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material';

import { COLORS, WARNING_CHIP } from '../IndividualPlan/tokens';
import { UPLOAD_KIND_OPTIONS, type UploadKind, type UploadedFile } from './uploadedFiles';

export type SisMatchStatus = 'required' | 'matched';

type UploadedDataCardProps = {
  files: UploadedFile[];
  onAddFiles: (files: FileList | null) => void;
  onRequestDelete: (file: UploadedFile) => void;
  onSelectKind?: (file: UploadedFile, kind: UploadKind) => void;
  onStartMatching?: (file: UploadedFile) => void;
  sisStatusByKey?: Record<string, SisMatchStatus>;
  setAsideUploadIds?: string[];
  loading?: boolean;
};

const MATCHED_FG = '#1F4E10';

export function UploadedDataCard({
  files,
  onAddFiles,
  onRequestDelete,
  onSelectKind,
  onStartMatching,
  sisStatusByKey,
  setAsideUploadIds,
  loading = false,
}: UploadedDataCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAddFiles(event.target.files);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        borderRadius: 2,
        border: `1px solid ${COLORS.hairlineSoft}`,
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: COLORS.muted, mb: 2 }}>
        Uploaded Data
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          borderRadius: 2,
          border: '1px dashed rgba(8, 8, 27, 0.25)',
          p: { xs: 2.5, md: 3 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 140,
            height: 140,
            flexShrink: 0,
            borderRadius: 1.5,
            bgcolor: '#D7D7F4',
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 64, color: COLORS.ink }} />
        </Box>

        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <FileUploadOutlinedIcon sx={{ fontSize: 32, color: COLORS.muted, mb: 1 }} />
          <Typography sx={{ color: COLORS.muted, fontSize: 15, mb: 2 }}>
            Select one or more .csv files to upload
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            multiple
            hidden
            onChange={handleChange}
          />
          <Button
            variant="contained"
            onClick={() => inputRef.current?.click()}
            sx={{
              bgcolor: '#1F1F70',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#18185A' },
            }}
          >
            Select Files
          </Button>
        </Box>
      </Box>

      {files.length === 0 && loading ? (
        <Box
          role="status"
          aria-label="Loading uploaded files"
          aria-busy
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5, px: 2, py: 1.75 }}
        >
          <CircularProgress size={18} sx={{ color: COLORS.muted }} />
          <Typography sx={{ fontSize: 14, color: COLORS.muted }}>Loading uploaded files…</Typography>
        </Box>
      ) : (
        files.length > 0 && (
          <Stack spacing={1.25} sx={{ mt: 2.5 }}>
            {files.map((file) => {
              const sisStatus =
                file.kind === 'student_roster' ? sisStatusByKey?.[file.clientKey ?? file.id] : undefined;
              const setAside = setAsideUploadIds?.includes(file.id) === true;
              return (
                <Box
                  key={file.clientKey ?? file.id}
                  data-testid="uploaded-file-row"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: `1px solid ${COLORS.hairlineSoft}`,
                    bgcolor: '#FFFFFF',
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ color: COLORS.muted, fontSize: 22 }} />
                  <Typography sx={{ fontSize: 14, color: COLORS.ink }}>{file.name}</Typography>
                  <Box sx={{ flex: 1 }} />
                  {setAside && (
                    <Chip
                      label="Not used · IDs don’t match"
                      size="small"
                      sx={{
                        bgcolor: WARNING_CHIP.bg,
                        color: WARNING_CHIP.fg,
                        fontWeight: 700,
                        fontSize: 12,
                        '& .MuiChip-label': { px: 1.5 },
                      }}
                    />
                  )}
                  {sisStatus === 'required' && (
                    <>
                      <Chip
                        label="Column matching required"
                        size="small"
                        sx={{
                          bgcolor: WARNING_CHIP.bg,
                          color: WARNING_CHIP.fg,
                          fontWeight: 700,
                          fontSize: 12,
                          '& .MuiChip-label': { px: 1.5 },
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => onStartMatching?.(file)}
                        aria-label={`Start column matching for ${file.name}`}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: '#D4D4DD',
                          color: COLORS.ink,
                          px: 2.5,
                          borderRadius: 1.5,
                          '&:hover': { borderColor: COLORS.ink, bgcolor: 'transparent' },
                        }}
                      >
                        Start
                      </Button>
                    </>
                  )}
                  {sisStatus === 'matched' && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: MATCHED_FG }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MATCHED_FG }}>
                        Columns matched
                      </Typography>
                    </Stack>
                  )}
                  <Select
                    size="small"
                    displayEmpty
                    value={file.kind ?? ''}
                    onChange={(e) => onSelectKind?.(file, e.target.value as UploadKind)}
                    inputProps={{ 'aria-label': `File type for ${file.name}` }}
                    sx={{ minWidth: 200, fontSize: 13, bgcolor: COLORS.bg }}
                  >
                    <MenuItem value="" disabled>
                      Select type
                    </MenuItem>
                    {UPLOAD_KIND_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <IconButton
                    size="small"
                    onClick={() => onRequestDelete(file)}
                    aria-label={`Delete ${file.name}`}
                    sx={{ color: COLORS.muted }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )
      )}
    </Box>
  );
}
